import React from "react";

// Tiny schematic drawings for the "Example" boxes in the Flexible settings.
// Two mini panels side by side (e.g. Off / On), each 96 x 70 units.

const C = {
  plate: "#eef1f2",
  edge: "#c9d0d3",
  duct: "#5c9495",
  a: "#7fa7c4",     // device type A
  b: "#d6a26a",     // device type B
  c: "#a7b86b",     // device type C
  big: "#9aa5a4",   // tall device (drive)
  text: "#5f6b72",
};

function Panel({ x, caption, children }) {
  return (
    <g transform={`translate(${x},0)`}>
      <rect x="0" y="0" width="96" height="70" rx="3" fill={C.plate} stroke={C.edge} />
      {children}
      <text x="48" y="82" textAnchor="middle" fontSize="9" fill={C.text}>{caption}</text>
    </g>
  );
}

const Box = ({ x, y, w, h, fill }) => <rect x={x} y={y} width={w} height={h} rx="1" fill={fill} />;
const DuctH = ({ y }) => <rect x="4" y={y} width="88" height="3" fill={C.duct} />;
const DuctV = ({ x, y = 4, h }) => <rect x={x} y={y} width="3" height={h} fill={C.duct} />;

// n small units stacked in a vertical column (terminals on a vertical rail)
const Column = ({ x, y, n, fill, unit = 3 }) => (
  <g>
    {Array.from({ length: n }, (_, i) => (
      <rect key={i} x={x} y={y + i * unit} width="9" height={unit - 0.6} fill={fill} />
    ))}
  </g>
);

function Diagram({ label, children }) {
  return (
    <svg viewBox="0 0 204 88" className="ssp-diagram" role="img" aria-label={label}>
      {children}
    </svg>
  );
}

/** Sections to look ahead: 0 = one band per section, 2 = A, B, C share a band */
export function LookaheadDiagram() {
  return (
    <Diagram label="Look ahead 0: three sections stacked. Look ahead 2: three sections side by side.">
      <Panel x={2} caption="0 – one band each">
        <Box x={6} y={5} w={40} h={12} fill={C.a} />
        <DuctH y={19} />
        <Box x={6} y={25} w={30} h={12} fill={C.b} />
        <DuctH y={39} />
        <Box x={6} y={45} w={24} h={12} fill={C.c} />
        <DuctH y={59} />
      </Panel>
      <Panel x={106} caption="2 – sharing one band">
        <Box x={6} y={5} w={30} h={12} fill={C.a} />
        <DuctV x={38} h={14} />
        <Box x={43} y={5} w={22} h={12} fill={C.b} />
        <DuctV x={67} h={14} />
        <Box x={72} y={5} w={18} h={12} fill={C.c} />
        <DuctH y={19} />
      </Panel>
    </Diagram>
  );
}

/** Use all available space: off = band follows the first section, on = band grows, rows wrap */
export function UseAllSpaceDiagram() {
  return (
    <Diagram label="Off: breakers in one row, drive in its own band. On: drive and two rows of breakers share a taller band.">
      <Panel x={2} caption="Off">
        <Box x={6} y={5} w={70} h={10} fill={C.a} />
        <DuctH y={17} />
        <Box x={6} y={23} w={26} h={30} fill={C.big} />
        <DuctH y={55} />
      </Panel>
      <Panel x={106} caption="On">
        <Box x={6} y={5} w={36} h={10} fill={C.a} />
        <rect x="6" y="18" width="36" height="2" fill={C.duct} />
        <Box x={6} y={23} w={36} h={10} fill={C.a} />
        <DuctV x={45} h={32} />
        <Box x={52} y={5} w={26} h={30} fill={C.big} />
        <DuctH y={37} />
      </Panel>
    </Diagram>
  );
}

/** Keep each device type on one rail: on = one column per type, off = types share */
export function KeepTypeDiagram() {
  return (
    <Diagram label="On: terminal type A and type B in two columns. Off: both types in one column.">
      <Panel x={2} caption="On – column per type">
        <Column x={10} y={5} n={17} fill={C.a} />
        <DuctV x={24} h={56} />
        <Column x={32} y={5} n={5} fill={C.b} />
        <DuctH y={62} />
      </Panel>
      <Panel x={106} caption="Off – types share">
        <Column x={10} y={5} n={14} fill={C.a} />
        <Column x={10} y={47} n={5} fill={C.b} />
        <DuctH y={62} />
      </Panel>
    </Diagram>
  );
}

/** Units per vertical rail: 0 = one long column, 20 = columns of 20 */
export function UnitsPerRailDiagram() {
  return (
    <Diagram label="No limit: one long column. Limit: three equal columns.">
      <Panel x={2} caption="0 – no limit">
        <Column x={10} y={5} n={18} fill={C.a} />
        <DuctH y={62} />
      </Panel>
      <Panel x={106} caption="20 – columns of 20">
        <Column x={10} y={5} n={6} fill={C.a} />
        <DuctV x={24} h={22} />
        <Column x={32} y={5} n={6} fill={C.a} />
        <DuctV x={46} h={22} />
        <Column x={54} y={5} n={6} fill={C.a} />
        <DuctH y={27} />
      </Panel>
    </Diagram>
  );
}
